import { useState, type ReactElement } from 'react';
import type { BlogOverviewDto } from '@gr/core/platform/admin/dto';
import { useT } from '../../../../app/di';
import { formatDate, formatDateTime, formatFileSize, formatNumber } from '../../../../shared/format';
import { DataTable } from '../../../../ui-kit/DataTable/DataTable';
import { EmptyState } from '../../../../ui-kit/EmptyState/EmptyState';
import { BareInput, BareSelect } from '../../../../ui-kit/Field/Field';
import { filterPlatformBlogs, PLATFORM_BLOG_SORTS, PlatformBlogSort, sortPlatformBlogs } from '../model/platform-blogs';
import styles from './PlatformBlogs.module.scss';

export interface PlatformBlogsProps {
  blogs: readonly BlogOverviewDto[];
  /** Месяц трафика, ГГГГ-ММ. */
  month: string;
}

/** ADM-UC-2: все блоги платформы со счётчиками — поиск по названию и владельцу, сортировка. */
export function PlatformBlogs({ blogs, month }: PlatformBlogsProps): ReactElement {
  const { t, locale } = useT();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState(PlatformBlogSort.Name);
  const n = (value: number): string => formatNumber(value, locale);
  const size = (bytes: number): string => formatFileSize(bytes, locale);
  const shown = sortPlatformBlogs(filterPlatformBlogs(blogs, query), sort, locale);

  return (
    <div className="u-stack">
      <div className={styles.filters}>
        <div className={styles.search}>
          <BareInput label={t('admin.blogs.search')} placeholder={t('admin.blogs.search')} type="search" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div className={styles.sort}>
          <BareSelect
            label={t('admin.blogs.sort')}
            value={sort}
            onChange={(e) => setSort(e.target.value as PlatformBlogSort)}
            options={PLATFORM_BLOG_SORTS.map((value) => ({ value, label: t(`admin.blogs.sorts.${value}`) }))}
          />
        </div>
      </div>
      <p className="u-muted u-small" aria-live="polite">
        {t('admin.blogs.found', { shown: n(shown.length), total: n(blogs.length) })}
      </p>
      <DataTable
        label={t('admin.blogs.title')}
        rows={shown}
        rowKey={(blog) => blog.id}
        empty={<EmptyState title={query.trim() ? t('admin.blogs.nothingFound') : t('admin.blogs.empty')} />}
        columns={[
          { key: 'name', header: t('admin.blogs.name'), primary: true, cell: (blog) => blog.name },
          { key: 'owners', header: t('admin.blogs.owners'), cell: (blog) => blog.owners.join(', ') || '—' },
          { key: 'members', header: t('admin.blogs.members'), align: 'end', cell: (blog) => n(blog.members) },
          { key: 'posts', header: t('admin.blogs.posts'), align: 'end', cell: (blog) => n(blog.posts) },
          { key: 'entities', header: t('admin.blogs.entities'), align: 'end', cell: (blog) => `${n(blog.entityTypes)} / ${n(blog.entities)}` },
          { key: 'media', header: t('admin.blogs.media'), align: 'end', cell: (blog) => `${n(blog.media)} · ${size(blog.mediaBytes)}` },
          { key: 'publications', header: t('admin.blogs.publications'), align: 'end', cell: (blog) => `${n(blog.publications.planned)} → ${n(blog.publications.published)}` },
          { key: 'traffic', header: t('admin.blogs.traffic', { month }), align: 'end', cell: (blog) => size(blog.trafficBytesThisMonth) },
          { key: 'created', header: t('admin.blogs.created'), cell: (blog) => formatDate(blog.createdAt, locale) },
          { key: 'changed', header: t('admin.blogs.lastChanged'), cell: (blog) => formatDateTime(blog.lastChangedAt, locale) },
        ]}
      />
    </div>
  );
}
